import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { IonicModule, ModalController } from '@ionic/angular';

import { LoadingService } from '@core/services/loading/loading.service';
import { AccountVisibilityService } from '@core/services/products/account-visibility.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { Product, ProductTypes } from '@shared/models/products.model';
import { SharedModule } from '@shared/shared.module';

@Component({
    selector: 'app-hide-statements-inline',
    templateUrl: './hide-statements-inline.component.html',
    styleUrls: ['./hide-statements-inline.component.scss'],
    imports: [CommonModule, IonicModule, FormsModule, SharedModule]
})
export class HideStatementsInlineComponent implements OnInit {
  @Input() product!: Product;

  @Output() toggleChange = new EventEmitter<boolean>();

  private readonly modalController = inject(ModalController);
  private readonly loadingService = inject(LoadingService);
  private readonly accountVisibilityService = inject(AccountVisibilityService);

  // Пытаемся получить product из инжектора (если передан через NgComponentOutlet)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly injectedProduct = inject<Product | null>('PRODUCT_DATA' as any, { optional: true });
  private initialized = false;

  // Состояние компонента
  protected isEnabled = false; // Запрет установлен (hiddenExtract = true)
  protected isDisabled = false; // Toggle disabled (forbiddenChange = true)

  ngOnInit(): void {
    // Если product не передан через @Input, пытаемся получить из инжектора
    if (!this.product && this.injectedProduct) {
      this.product = this.injectedProduct;
    }

    // Проверяем, что это счет - компонент должен отображаться только для счетов и карт
    if (
      this.product &&
      (this.product.productType === ProductTypes.ACCOUNT ||
        this.product.productType === ProductTypes.SAVE_ACCOUNT ||
        this.product.productType === ProductTypes.METAL_ACCOUNT ||
        this.product.productType === ProductTypes.DEPOSIT ||
        this.product.productType === ProductTypes.CARD)
    ) {
      if (!this.initialized) {
        this.initializeFromProduct();
        this.initialized = true;
      }
    }
  }

  protected async onToggleChange(event: CustomEvent): Promise<void> {
    const isChecked = event.detail.checked;

    // Если toggle выключается (isChecked === false), снимаем запрет на выписки
    if (!isChecked && this.isEnabled) {
      await this.removeForbidStatement();

      return;
    }

    // Если toggle включается, открываем модальное окно для установки запрета
    await this.openHideStatementsModal();
  }

  /**
   * Инициализация состояния из данных продукта
   */
  private initializeFromProduct(): void {
    if (!this.product) {
      return;
    }

    // hiddenExtract - запрет на отображение в выписках (toggle включён когда true)
    this.isEnabled = TypeUtilService.isTruthy(this.product.hiddenExtract);

    // forbiddenChange - запрет на изменение параметров (toggle disabled когда true)
    this.isDisabled = TypeUtilService.isTruthy(this.product.forbiddenChange);

    console.log('HideStatementsInline Debug:', {
      productId: this.product.id,
      hiddenExtract: this.product.hiddenExtract,
      forbiddenChange: this.product.forbiddenChange,
      isEnabled: this.isEnabled,
      isDisabled: this.isDisabled
    });
  }

  /**
   * Снятие запрета на отображение в выписках
   */
  private async removeForbidStatement(): Promise<void> {
    if (!this.product?.id) {
      console.error('Cannot remove forbid statement: product is undefined or missing id');

      return;
    }

    // Проверяем запрет на изменение ограничений (forbiddenChange)
    // Если установлен, не вызываем API (будет ошибка 3011)
    if (this.isDisabled) {
      console.warn('Cannot remove forbid statement: forbiddenChange is true for product', this.product.id);

      return;
    }

    try {
      this.loadingService.showLoader(true);

      // Вызываем API снятия запрета на выписки
      await this.accountVisibilityService.removeForbidStatement(this.product.id).toPromise();

      this.loadingService.showLoader(false);

      // Обновляем локальную модель
      this.product.hiddenExtract = 'false';

      // Сбрасываем состояние компонента
      this.isEnabled = false;

      this.toggleChange.emit(false);

      console.log('Запрет на выписки успешно снят с продукта:', this.product.id);
    } catch (error) {
      this.loadingService.showLoader(false);
      console.error('Ошибка снятия запрета на выписки:', error);

      // Восстанавливаем предыдущее состояние toggle при ошибке
      this.isEnabled = true;
    }
  }

  /**
   * Открывает модальное окно для установки запрета на выписки
   */
  private async openHideStatementsModal(): Promise<void> {
    if (!this.product) {
      return;
    }

    try {
      // Динамический импорт компонента
      const { HideStatementsModalComponent } = await import(
        '@pages/product/modals/hide-statements/hide-statements-modal.component'
      );

      const modal = await this.modalController.create({
        component: HideStatementsModalComponent,
        handle: false,
        cssClass: 'custom',
        initialBreakpoint: 1,
        componentProps: {
          product: this.product,
          staticHeight: true
        }
      });

      await modal.present();

      const { data } = await modal.onDidDismiss();

      // Если операция успешна, обновляем состояние
      if (data?.success) {
        this.product.hiddenExtract = 'true';
        this.isEnabled = true;
        this.toggleChange.emit(true);
      } else {
        // Возвращаем toggle в исходное состояние при отмене
        this.isEnabled = false;
      }
    } catch (error) {
      console.error('Ошибка открытия модального окна:', error);

      // Возвращаем toggle в исходное состояние при ошибке
      this.isEnabled = false;
    }
  }
}
